import {
  Button,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  Icon,
  Input,
  Text,
} from "@chakra-ui/react";
import { useDynamicModalContext, useMultiForm } from "@umami/components";
import { clearStorage, useAsyncActionHandler, useResetState } from "@umami/state";
import { Controller, FormProvider } from "react-hook-form";

import { CheckmarkIcon, CloseIcon, FileUploadIcon } from "../../../assets/icons";
import { useColor } from "../../../styles/useColor";

import { SetupBackupPassword } from "@/components/SetupBackupPassword";
import { trackButtonClick } from "@/utils/analytics";

export const ImportBackupTab = () => {
  const color = useColor();

  const { openWith } = useDynamicModalContext();
  const { handleAsyncActionUnsafe } = useAsyncActionHandler();
  const resetAllState = useResetState();

  const form = useMultiForm<{
    file?: FileList;
  }>({
    mode: "all",
  });
  const {
    formState: { errors },
    handleSubmit,
  } = form;

  const fileData = form.watch("file");

  const handleBackupSubmit = async () =>
    handleAsyncActionUnsafe(async () => {
      if (fileData?.[0]) {
        const rawBackup = await fileData[0].text();

        trackButtonClick("continue_with_backup", {
          button_label: "Next",
          section: "Welcome",
          flow: "Onboarding",
          action: "button_click",
        });
        await openWith(<SetupBackupPassword isSetNewPassword={false} rawBackup={rawBackup} />);
      }
    }).catch(() => {
      resetAllState();
      clearStorage();
    });

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(handleBackupSubmit)}>
        <Flex flexDirection="column" gap="24px">
          <FormControl
            borderY="1px solid"
            borderTopColor={color("100")}
            borderBottomColor={color("100")}
            isInvalid={!!errors.file}
          >
            {fileData?.length ? (
              <>
                <Center gap="10px" height="72px">
                  <Text color={color("black")} fontWeight="600" size="xl">
                    Upload complete
                  </Text>
                  <Icon as={CheckmarkIcon} color={color("green")} />
                </Center>
                <Button
                  width="full"
                  fontSize="14px"
                  fontWeight="400"
                  border="none"
                  borderRadius="0"
                  onClick={() => form.setValue("file", undefined)}
                  rightIcon={<Icon as={CloseIcon} color={color("400")} />}
                >
                  {fileData[0].name}
                </Button>
              </>
            ) : (
              <Button
                width="full"
                height="72px"
                borderRadius="0"
                _hover={{
                  bg: color("100"),
                }}
                onClick={() => {
                  document.getElementById("backup-file-input")?.click();
                }}
                rightIcon={<Icon as={FileUploadIcon} color={color("400")} />}
                variant="ghost"
              >
                Choose file
              </Button>
            )}

            <Controller
              control={form.control}
              name="file"
              render={({ field: { onChange, value } }) => (
                <Input
                  key={value ? "has-file" : "no-file"}
                  display="none"
                  accept=".json,application/JSON"
                  data-testid="file-input"
                  id="backup-file-input"
                  onChange={e => onChange(e.target.files)}
                  type="file"
                  variant="unstyled"
                />
              )}
            />
            {errors.file && <FormErrorMessage>{errors.file.message}</FormErrorMessage>}
          </FormControl>
          <Button
            width="full"
            isDisabled={!fileData}
            isLoading={false}
            type="submit"
            variant="primary"
          >
            Next
          </Button>
        </Flex>
      </form>
    </FormProvider>
  );
};
